import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../api/api.service';
import { BETTER_AUTH_CLIENT } from './better-auth-client';
import { environment } from '../../../environments/environment';

export type Role = 'admin' | 'manager' | 'caseworker' | 'support' | 'readonly';

export interface MacUser {
  id: string;
  email: string;
  role: Role;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  image?: string | null;
  departmentId?: string;
  departmentName?: string;
  twoFactorEnabled?: boolean;
}

/** Result of a sign-in attempt: either fully signed in, or a 2FA code is required. */
export type SignInResult = { status: 'ok'; user: MacUser } | { status: 'two-factor' };

/** Returned when enabling 2FA — the otpauth URI to render as a QR code plus one-time backup codes. */
export interface TwoFactorEnrollment {
  totpURI: string;
  backupCodes: string[];
}

export interface MeResponse {
  user: MacUser;
  features: string[];
  caseProgrammeAccess?: {
    unrestricted: boolean;
    programmes: string[];
  };
}

export interface ProfileUpdateInput {
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly authClient = inject(BETTER_AUTH_CLIENT);

  private readonly _user = signal<MacUser | null>(null);
  private readonly _features = signal<Set<string>>(new Set());
  private readonly _caseProgrammeAccess = signal<{ unrestricted: boolean; programmes: string[] }>({
    unrestricted: true,
    programmes: [],
  });
  private readonly _loaded = signal(false);

  readonly user = this._user.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly caseProgrammeAccess = this._caseProgrammeAccess.asReadonly();

  can(feature: string): boolean {
    return this._features().has(feature);
  }

  allowedCaseProgrammes<T extends { value: string }>(programmes: T[]): T[] {
    const access = this._caseProgrammeAccess();
    if (access.unrestricted) return programmes;
    const allowed = new Set(access.programmes);
    return programmes.filter((p) => allowed.has(p.value));
  }

  /**
   * Whether the current user may CREATE or EDIT cases of the given programme.
   * Reads are unrestricted system-wide; this gates write actions so the UI can
   * hide edit controls on case types the caseworker isn't assigned to. Mirrors
   * the backend's `canAccessCaseProgramme` check.
   */
  canManageCaseProgramme(programme: string | null | undefined): boolean {
    if (!programme) return false;
    const access = this._caseProgrammeAccess();
    return access.unrestricted || access.programmes.includes(programme);
  }

  async loadCurrentUser(): Promise<MacUser | null> {
    try {
      const res = await firstValueFrom(this.api.get<MeResponse>('/api/me'));
      this._user.set(res.user);
      this._features.set(new Set(res.features));
      this._caseProgrammeAccess.set(res.caseProgrammeAccess ?? { unrestricted: true, programmes: [] });
      return res.user;
    } catch {
      this._user.set(null);
      this._features.set(new Set());
      this._caseProgrammeAccess.set({ unrestricted: true, programmes: [] });
      return null;
    } finally {
      this._loaded.set(true);
    }
  }

  async signIn(email: string, password: string): Promise<SignInResult> {
    const res = await firstValueFrom(
      this.api.post<{ twoFactorRedirect?: boolean }>('/api/auth/sign-in/email', { email, password }, false),
    );
    // When the account has 2FA enabled (and this device isn't trusted), Better
    // Auth withholds the session and signals that a code is required instead.
    if (res?.twoFactorRedirect) return { status: 'two-factor' };
    const user = await this.loadCurrentUser();
    if (!user) throw new Error('Sign-in succeeded but session could not be loaded');
    return { status: 'ok', user };
  }

  /** Completes a 2FA sign-in with a TOTP code from the authenticator app. */
  async verifyTwoFactorTotp(code: string, trustDevice: boolean): Promise<MacUser> {
    const res = await this.authClient.twoFactor.verifyTotp({ code, trustDevice });
    if (res.error) throw res.error;
    const user = await this.loadCurrentUser();
    if (!user) throw new Error('Verification succeeded but session could not be loaded');
    return user;
  }

  /** Completes a 2FA sign-in with a one-time backup code (device is not trusted). */
  async verifyTwoFactorBackupCode(code: string): Promise<MacUser> {
    const res = await this.authClient.twoFactor.verifyBackupCode({ code });
    if (res.error) throw res.error;
    const user = await this.loadCurrentUser();
    if (!user) throw new Error('Verification succeeded but session could not be loaded');
    return user;
  }

  /**
   * Begins 2FA enrolment. Returns the otpauth URI (render as a QR code) and the
   * backup codes. 2FA is NOT active until confirmed with {@link confirmTwoFactor}.
   */
  async enableTwoFactor(password: string): Promise<TwoFactorEnrollment> {
    // Remove any stale verified record left by the legacy admin-reset path
    // before Better Auth creates the new enrolment secret.
    await firstValueFrom(this.api.post('/api/me/two-factor/prepare', {}, false));
    const res = await this.authClient.twoFactor.enable({ password });
    if (res.error) throw res.error;
    return res.data as TwoFactorEnrollment;
  }

  /** Confirms enrolment with the first TOTP code, which activates 2FA on the account. */
  async confirmTwoFactor(code: string): Promise<void> {
    const res = await this.authClient.twoFactor.verifyTotp({ code });
    if (res.error) throw res.error;
    const user = await this.loadCurrentUser();
    if (!user?.twoFactorEnabled) {
      throw new Error('Two-factor verification succeeded, but the account was not enabled');
    }
  }

  /** Disables 2FA for the current user (requires their password). */
  async disableTwoFactor(password: string): Promise<void> {
    const res = await firstValueFrom(
      this.api.post<{ status: boolean }>(
        '/api/auth/two-factor/disable',
        { password },
        false,
      ),
    );
    if (res.status !== true) throw new Error('Two-factor authentication was not disabled');
    this.patchCurrentUser({ twoFactorEnabled: false });
  }

  /** Generates a fresh set of backup codes, invalidating the previous set. */
  async regenerateBackupCodes(password: string): Promise<string[]> {
    const res = await firstValueFrom(
      this.api.post<{ status: boolean; backupCodes: string[] }>(
        '/api/auth/two-factor/generate-backup-codes',
        { password },
        false,
      ),
    );
    if (res.status !== true || !Array.isArray(res.backupCodes) || res.backupCodes.length === 0) {
      throw new Error('The server did not return new backup codes');
    }
    return res.backupCodes;
  }

  async signOut(): Promise<void> {
    try {
      await firstValueFrom(this.api.post('/api/auth/sign-out', {}, false));
    } finally {
      this._user.set(null);
      this._features.set(new Set());
      this._caseProgrammeAccess.set({ unrestricted: true, programmes: [] });
    }
  }

  async updateProfile(input: ProfileUpdateInput): Promise<MacUser> {
    const res = await firstValueFrom(this.api.patch<{ user: MacUser }>('/api/me', input));
    this._user.set(res.user);
    return res.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await firstValueFrom(
      this.api.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      }),
    );
  }

  async requestPasswordReset(email: string, redirectTo: string): Promise<void> {
    await firstValueFrom(
      this.api.post('/api/auth/request-password-reset', {
        email,
        redirectTo,
      }, false),
    );
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await firstValueFrom(
      this.api.post('/api/auth/reset-password', {
        token,
        newPassword,
      }, false),
    );
  }

  async uploadAvatar(file: File): Promise<string | null> {
    const form = new FormData();
    form.set('file', file);
    const res = await firstValueFrom(
      this.api.post<{ item: { image: string | null } }>('/api/me/avatar', form),
    );
    this.patchCurrentUser({ image: res.item.image });
    return res.item.image;
  }

  async removeAvatar(): Promise<void> {
    await firstValueFrom(this.api.delete<{ item: { image: null } }>('/api/me/avatar'));
    this.patchCurrentUser({ image: null });
  }

  fileUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (/^https?:\/\//i.test(path) || path.startsWith('data:') || path.startsWith('blob:')) return path;
    return `${environment.apiUrl}${path}`;
  }

  private patchCurrentUser(patch: Partial<MacUser>): void {
    const user = this._user();
    if (user) this._user.set({ ...user, ...patch });
  }
}
